import React, { createContext, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { isEqual } from "lodash";

import { LanguageTranslateValues } from "../models/language.translate.values";
import { Languages as LanguagesType } from "../models/languages";
import { Filters, Searches } from "../models/shipeezi.table";
import LanguageTranslateValuesService from "../services/language.translate.values.service";
// import { useAppState } from "../state";

interface LanguageContextProps {
  language?: LanguagesType;
  translateLanguages?: LanguageTranslateValues[];
  updatedTranslate?: LanguageTranslateValues[];
  isFetchingTranslate?: boolean;
  pageTranslate: number;
  pageTranslateSize: number;
  translateTotal: number;
  setSearches?: (value?: Searches) => void;
  setFilters?: (value: Filters) => void;
  setLanguage: (value?: LanguagesType) => void;
  setPageTranslate?: (value: number) => void;
  setPageTranslateSize?: (value: number) => void;
  setCurrentTranslatePage?: (value: number) => void;
  setTranslateLanguages: (value?: LanguageTranslateValues[]) => void;
  setUpdatedTranslate: (value: LanguageTranslateValues[]) => void;
  handleUpdateLanguage: (attribute: string, value: any) => void;
  handleUpdateTranslate: (value: LanguageTranslateValues) => void;
}

const LanguageContext = createContext<LanguageContextProps>({} as LanguageContextProps);

interface LanguageProviderProps {
  children: React.ReactChild;
}

const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const [language, setLanguage] = useState<LanguagesType>();
  const [translateLanguages, setTranslateLanguages] = useState<LanguageTranslateValues[]>();
  const [translateLanguagesDefault, setTranslateLanguagesDefault] = useState<
    LanguageTranslateValues[]
  >([]);
  const [updatedTranslate, setUpdatedTranslate] = useState<LanguageTranslateValues[]>([]);
  const [pageTranslate, setPageTranslate] = useState<number>(0);
  const [pageTranslateSize, setPageTranslateSize] = useState<number>(20);
  const [translateTotal, setTranslateTotal] = useState<number>(0);

  const [searches, setSearches] = useState<Searches>();
  const [filters, setFilters] = useState<Filters>();

  const setCurrentTranslatePage = (numberPage: number) => setPageTranslate(numberPage - 1);

  const handleUpdateLanguage = (attribute: string, value: any) => {
    setLanguage((state) =>
      state ? { ...state, [attribute]: value } : ({ [attribute]: value } as LanguagesType)
    );
  };

  const handleUpdateTranslate = (value: LanguageTranslateValues) => {
    const hasAlreadyExistInsideUpdatedTranslate = updatedTranslate?.find(
      (updateValue) => updateValue.id === value.id
    );

    const translateLanguage = translateLanguagesDefault?.find(
      (translateValue) => translateValue.id === value.id
    );

    if (isEqual(translateLanguage, value)) {
      setUpdatedTranslate((prevState) =>
        prevState?.filter((prevStateMap) => prevStateMap.id !== translateLanguage?.id)
      );

      return;
    }

    if (hasAlreadyExistInsideUpdatedTranslate) {
      if (isEqual(hasAlreadyExistInsideUpdatedTranslate, value)) return;

      const newTranslateLanguage =
        updatedTranslate.filter(
          (updateValue) => updateValue.id !== hasAlreadyExistInsideUpdatedTranslate.id
        ) || [];

      newTranslateLanguage.push(value);

      setUpdatedTranslate(newTranslateLanguage);

      return;
    }

    setUpdatedTranslate([...updatedTranslate, value]);
  };

  const { isFetching } = useQuery(
    ["translate.list", pageTranslate, pageTranslateSize, searches, filters],
    () =>
      LanguageTranslateValuesService.getAll({
        page: pageTranslate,
        pageSize: pageTranslateSize,
        languageId: language?.id,
        defaultLanguageId: language?.defaultLanguageId,
        include: JSON.stringify([{ association: "language" }, { association: "user" }]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!language?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          setTranslateLanguages(response.data);
          setTranslateLanguagesDefault(response.data);
        }
      },
    }
  );

  useQuery(
    ["translate.values.total", searches, filters],
    () =>
      LanguageTranslateValuesService.count({
        languageId: language?.id,
        defaultLanguageId: language?.defaultLanguageId,
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!language?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        setTranslateTotal(response?.data || 0);
      },
    }
  );

  return (
    <LanguageContext.Provider
      value={{
        pageTranslate,
        pageTranslateSize,
        language,
        updatedTranslate,
        translateLanguages,
        isFetchingTranslate: isFetching,
        translateTotal,
        setPageTranslate,
        setCurrentTranslatePage,
        setPageTranslateSize,
        setSearches,
        setFilters,
        setLanguage,
        setUpdatedTranslate,
        setTranslateLanguages,
        handleUpdateLanguage,
        handleUpdateTranslate,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export { LanguageContext, LanguageProvider };
